import { ButtonUtility, Table } from "@light-technology/components";
import { Edit05, Trash01 } from "@untitledui/icons";
import { api } from "../../state/api";
import { Role } from "../../state/api.types";
import { useAuthHasRole } from "../../state/hooks";
import { getPrimaryRole, ROLE_TO_STRING } from "../../utilities/role";
import { ConfirmRemoveModal } from "../shared/ConfirmRemoveModal";
import { TruncatedText } from "../shared/TruncatedText";
import { EditPermissionsModal } from "./EditPermissionsModal";

type MembersListProps = {
  orgSlug: string;
  currentUserEmail: string;
};

export const MembersList = ({
  orgSlug,
  currentUserEmail,
}: MembersListProps) => {
  const membersQuery = api.useGetMembersQuery(orgSlug);
  const [removeOrgMember] = api.useRemoveOrgMemberMutation();
  const [syncUserRoles, syncUserRolesState] = api.useSyncUserRolesMutation();
  const isAdmin = useAuthHasRole(Role.OrgAdmin);

  const members = membersQuery.data?.data ?? [];
  const sortedMembers = members.toSorted((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <>
      {sortedMembers.map((user) => {
        const fullName = `${user.first_name} ${user.last_name}`;
        const userRole = getPrimaryRole(user.roles);
        const isCurrentUser = user.email === currentUserEmail;
        const canModify = isAdmin && !isCurrentUser;

        return (
          <Table.Row key={user.uuid}>
            <Table.Cell>
              <TruncatedText
                content={fullName}
                className="font-medium text-primary"
              />
            </Table.Cell>
            <Table.Cell>
              <TruncatedText content={user.email} className="text-tertiary" />
            </Table.Cell>
            <Table.Cell>{ROLE_TO_STRING[userRole]}</Table.Cell>
            <Table.Cell>
              {canModify && (
                <div className="flex items-center gap-2">
                  <EditPermissionsModal
                    displayName={fullName}
                    currentRole={userRole}
                    isLoading={syncUserRolesState.isLoading}
                    isSuccess={syncUserRolesState.isSuccess}
                    isFetching={membersQuery.isFetching}
                    error={syncUserRolesState.error}
                    onSubmit={(role) => {
                      syncUserRoles({
                        userUUID: user.uuid,
                        roles: [role],
                      });
                    }}
                    onReset={() => {
                      syncUserRolesState.reset();
                    }}
                  >
                    <ButtonUtility size="xs" color="tertiary" icon={Edit05} />
                  </EditPermissionsModal>
                  <ConfirmRemoveModal
                    title={`Are you sure you want to remove ${fullName} from your team?`}
                    description="They will no longer have access to your team's apps."
                    onConfirm={() => {
                      removeOrgMember(user.uuid);
                    }}
                    data-ph-capture-attribute-action="remove-team-member"
                  >
                    <ButtonUtility size="xs" color="tertiary" icon={Trash01} />
                  </ConfirmRemoveModal>
                </div>
              )}
            </Table.Cell>
          </Table.Row>
        );
      })}
    </>
  );
};
